import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Matches a bare URL: an explicit http(s):// link or a www. host, stopping at
// whitespace/angle-brackets/parens and trimming trailing sentence punctuation.
const BARE_URL_RE = /(https?:\/\/|www\.)[^\s<>()]+[^\s<>().,;:!?'"]/gi;

/** Normalise a user-entered link target into a real href (add a scheme, turn a
 *  bare address into mailto:). */
export function normalizeHref(input: string): string {
  const t = input.trim();
  if (!t) return t;
  if (/^[a-z][a-z0-9+.-]*:/i.test(t)) return t; // already has a scheme
  if (/^www\./i.test(t)) return `https://${t}`;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return `mailto:${t}`;
  return `https://${t}`;
}

/** Wrap bare URLs in a block of HTML with real <a> tags so typed/pasted links
 *  go out clickable. Walks the parsed DOM and only touches text nodes that
 *  aren't already inside an anchor, so existing links and tag attributes are
 *  left untouched. Browser-only (uses the DOM); returns the input unchanged on
 *  the server or when there's nothing URL-shaped to link. */
export function autolinkHtml(html: string): string {
  if (!html || typeof document === 'undefined') return html;
  if (!/https?:\/\/|www\./i.test(html)) return html;

  const tpl = document.createElement('template');
  tpl.innerHTML = html;

  const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) textNodes.push(n as Text);

  for (const node of textNodes) {
    const text = node.nodeValue ?? '';
    if (!/https?:\/\/|www\./i.test(text)) continue;
    // Don't re-link inside an existing anchor (or code/pre, where a URL is
    // likely meant literally).
    if (node.parentElement?.closest('a, code, pre')) continue;

    BARE_URL_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    let last = 0;
    let frag: DocumentFragment | null = null;
    while ((match = BARE_URL_RE.exec(text))) {
      frag ??= document.createDocumentFragment();
      const url = match[0];
      if (match.index > last) {
        frag.appendChild(document.createTextNode(text.slice(last, match.index)));
      }
      const a = document.createElement('a');
      a.setAttribute('href', normalizeHref(url));
      a.textContent = url;
      frag.appendChild(a);
      last = match.index + url.length;
    }
    if (frag) {
      if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
      node.parentNode?.replaceChild(frag, node);
    }
  }

  return tpl.innerHTML;
}
